/**
 * Runtime constants, with no dependencies.
 *
 * This file exists for one reason: the widget needs a handful of *values* —
 * a character limit, a socket path, a heartbeat interval — and importing them
 * from the barrel would drag in Zod and every schema in the package. That is
 * roughly 25 KB gzipped delivered to a visitor who only wants to type a
 * question, which is the same mistake as the reference system's single 878 KB
 * bundle, just smaller.
 *
 * So: values that clients need live here; schemas live next door and import
 * from here. Types are erased at build time and can be imported from anywhere.
 */

/**
 * Message and payload limits, enforced identically at the client composer, the
 * REST boundary and the socket handler. The reference system advertises an
 * 8 MB socket payload ceiling with no application-level limit at all — finding
 * S-4 / L-4 — so an over-long message fails at the transport with no
 * explanation anyone can act on.
 */
export const LIMITS = {
  /** Longest chat message a visitor or agent may send. */
  messageChars: 4000,
  /** Longest single text we will hand to a translation provider. */
  translateChars: 5000,
  /** Hard ceiling on any single socket payload, in bytes. */
  socketPayloadBytes: 64 * 1024,
  /** Bulk dictionary import cap per request. */
  dictionaryBulkTerms: 200,
  /** Visitor messages before the lead form is offered. */
  leadPromptAfterMessages: 3,
  /**
   * How many languages one visitor may select at once.
   *
   * A visitor picks the languages they read; the first is the one replies are
   * translated into. The cap exists because every extra language is another
   * provider call on every agent reply, and nobody reads five.
   */
  visitorLanguages: 5,
} as const;

/**
 * Audio constants, Observed from the reference system's live configuration and
 * reused because they are hard-won and correct. They live here so the API, the
 * translator worker and the clients cannot disagree about them.
 */
export const AUDIO = {
  /** Silence that means "they have finished talking". The largest single delay. */
  endpointSilenceMs: 700,
  /** After this a sentence is cut and translated anyway. */
  maxUtteranceMs: 5000,
  /** Audio retained from before the gate opens, so the first word is not clipped. */
  preRollMs: 900,
  sampleRate: 16_000,
  /** Far-end voice is ducked, not muted, while a translation plays. */
  duckToPercent: 15,
} as const;

export const SOCKET_PATH = '/socket.io';
export const VISITOR_NAMESPACE = '/visitor';
export const AGENT_NAMESPACE = '/agent';

/** Application-level heartbeat, on top of the transport ping. */
export const HEARTBEAT_INTERVAL_MS = 30_000;
